import { useEffect, useRef, useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STROKE = 1.5;

/** Inline-editable page title. Admin/recruiter can rename; viewers and
 * hiring managers see it read-only (they never see the pencil).
 *
 * Rename call is PATCH-driven — pass a `saveFn(newValue)` that returns the
 * updated resource. Successful save writes an audit row server-side; this
 * component only handles the UX.
 */
export default function EditableTitle({
  value,
  canEdit = false,
  onSave,
  className = "",
  placeholder = "Untitled",
  maxLength = 200,
  testid = "editable-title",
  minLength = 1,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    const next = (draft || "").trim();
    if (!next || next.length < minLength) {
      toast.error("Name cannot be empty");
      return;
    }
    if (next === (value || "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className={`group inline-flex items-baseline gap-2 ${className}`}>
        <span data-testid={testid}>{value || placeholder}</span>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="hidden group-hover:inline-flex items-center text-ink-400 hover:text-ink-700"
            aria-label="Rename"
            data-testid={`${testid}-edit`}
          >
            <Pencil className="h-4 w-4" strokeWidth={STROKE} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        disabled={saving}
        className="min-w-[280px] rounded-md border border-emerald-800/40 bg-white px-2 py-1 text-inherit font-inherit outline-none focus:ring-2 focus:ring-emerald-800/20"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        className="inline-flex items-center rounded-md bg-emerald-800 px-2 py-1 text-[12px] font-medium text-white hover:bg-emerald-900 disabled:opacity-60"
        data-testid={`${testid}-save`}
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={STROKE} /> : <Check className="h-3.5 w-3.5" strokeWidth={STROKE} />}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        className="inline-flex items-center rounded-md border border-ink-200 px-2 py-1 text-[12px] font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-60"
        data-testid={`${testid}-cancel`}
      >
        <X className="h-3.5 w-3.5" strokeWidth={STROKE} />
      </button>
    </div>
  );
}
